import React, { useRef, useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import * as THREE from 'three'
import gsap from 'gsap'
import { useAuth, SignInButton, UserButton } from '@clerk/clerk-react'

const Navbar = () => {
  const navRef      = useRef(null)
  const logoRef     = useRef(null)
  const linksRef    = useRef([])
  const mobileRef   = useRef(null)
  const canvasRef   = useRef(null)          // Three.js canvas lives here

  const { isSignedIn } = useAuth()
  const location       = useLocation()
  const [scrolled,   setScrolled]   = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  /* ── 1. Scroll detection ─────────────────────────────────── */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  /* ── 2. Three.js – green bird-particle canvas inside nav ── */
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const NAV_H = 72

    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true })
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

    const scene  = new THREE.Scene()
    const camera = new THREE.PerspectiveCamera(55, window.innerWidth / NAV_H, 0.1, 100)
    camera.position.z = 5

    const sync = () => {
      const w = window.innerWidth
      renderer.setSize(w, NAV_H, false)
      camera.aspect = w / NAV_H
      camera.updateProjectionMatrix()
    }
    sync()
    window.addEventListener('resize', sync)

    /* green glow sprite */
    const sc   = document.createElement('canvas')
    sc.width   = 48; sc.height = 48
    const ctx  = sc.getContext('2d')
    const grad = ctx.createRadialGradient(24, 24, 0, 24, 24, 24)
    grad.addColorStop(0,   'rgba(74,222,128,1)')    // #4ade80
    grad.addColorStop(0.35,'rgba(34,197,94,0.65)')  // #22c55e
    grad.addColorStop(1,   'rgba(22,163,74,0)')     // #16a34a
    ctx.fillStyle = grad
    ctx.fillRect(0, 0, 48, 48)
    const tex = new THREE.CanvasTexture(sc)

    /* particles */
    const COUNT     = 90
    const positions = new Float32Array(COUNT * 3)
    const velX      = new Float32Array(COUNT)
    const phase     = new Float32Array(COUNT)

    for (let i = 0; i < COUNT; i++) {
      positions[i * 3]     = (Math.random() - 0.5) * 26
      positions[i * 3 + 1] = (Math.random() - 0.5) * 2.4
      positions[i * 3 + 2] = (Math.random() - 0.5) * 3
      velX[i]  = (Math.random() * 0.4 + 0.15) * (Math.random() > 0.5 ? 1 : -1) * 0.006
      phase[i] = Math.random() * Math.PI * 2
    }

    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))

    const mat = new THREE.PointsMaterial({
      size:            0.10,
      map:             tex,
      transparent:     true,
      opacity:         0.8,
      sizeAttenuation: true,
      depthWrite:      false,
    })

    const pts = new THREE.Points(geo, mat)
    scene.add(pts)

    let t = 0, animId
    const pos = geo.attributes.position.array

    const tick = () => {
      animId = requestAnimationFrame(tick)
      t += 0.003
      for (let i = 0; i < COUNT; i++) {
        pos[i * 3]     += velX[i]
        pos[i * 3 + 1] += Math.sin(t + phase[i]) * 0.003
        if (pos[i * 3] >  13) pos[i * 3] = -13
        if (pos[i * 3] < -13) pos[i * 3] =  13
      }
      geo.attributes.position.needsUpdate = true
      pts.rotation.z = Math.sin(t * 0.08) * 0.015
      renderer.render(scene, camera)
    }
    tick()

    return () => {
      cancelAnimationFrame(animId)
      window.removeEventListener('resize', sync)
      geo.dispose(); mat.dispose(); tex.dispose(); renderer.dispose()
    }
  }, [])

  /* ── 3. GSAP entrance ────────────────────────────────────── */
  useEffect(() => {
    const tl = gsap.timeline({ delay: 0.1 })
    tl.fromTo(navRef.current,
      { y: -100, opacity: 0 },
      { y: 0, opacity: 1, duration: 1.2, ease: 'expo.out' }
    )
    .fromTo(logoRef.current,
      { x: -20, opacity: 0 },
      { x: 0, opacity: 1, duration: 0.7, ease: 'power3.out' },
      '-=0.6'
    )
    .fromTo(linksRef.current.filter(Boolean),
      { y: -12, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.5, stagger: 0.07, ease: 'power2.out' },
      '-=0.4'
    )
  }, [])

  /* ── 4. Mobile menu animation ────────────────────────────── */
  useEffect(() => {
    if (!mobileRef.current) return
    if (mobileOpen) {
      gsap.fromTo(mobileRef.current,
        { opacity: 0, y: -12, scaleY: 0.93 },
        { opacity: 1, y: 0, scaleY: 1, duration: 0.35, ease: 'power3.out', transformOrigin: 'top' }
      )
    }
  }, [mobileOpen])

  useEffect(() => { setMobileOpen(false) }, [location.pathname])

  /* ── derived state ───────────────────────────────────────── */
  const NAV_LINKS = ['Home', 'Blog', 'Gallery', 'Games', 'Profile']
  const isActive  = (link) =>
    link === 'Home' ? location.pathname === '/' : location.pathname === `/${link.toLowerCase()}`

  const isHome = location.pathname === '/'

  /*
   * showSolid = true  → dark frosted-glass  (scrolled, OR not on homepage)
   * showSolid = false → green particle look (homepage + not yet scrolled)
   */
  const showSolid = scrolled || !isHome

  /* colours change between the two states */
  const linkColor   = showSolid ? 'rgba(255,255,255,0.82)' : '#86efac'  // white vs bright green
  const logoSub     = showSolid ? 'rgba(255,255,255,0.32)' : 'rgba(134,239,172,0.55)'
  const dividerBg   = showSolid ? 'rgba(255,255,255,0.15)' : 'rgba(134,239,172,0.25)'

  /* ── render ──────────────────────────────────────────────── */
  return (
    <nav
      ref={navRef}
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500 overflow-hidden"
      style={{
        background: showSolid
          ? 'rgba(8,14,6,0.92)'
          : 'linear-gradient(135deg,rgba(5,28,8,0.82) 0%,rgba(10,42,12,0.72) 100%)',
        backdropFilter:       'blur(22px)',
        WebkitBackdropFilter: 'blur(22px)',
        borderBottom: showSolid
          ? '1px solid rgba(255,255,255,0.07)'
          : '1px solid rgba(74,222,128,0.18)',
        boxShadow: showSolid
          ? '0 4px 48px rgba(0,0,0,0.50)'
          : '0 4px 32px rgba(34,197,94,0.20)',
      }}
    >
      {/* ── Three.js canvas – always mounted, opacity swaps ── */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 pointer-events-none transition-opacity duration-500"
        style={{
          width:   '100%',
          height:  '100%',
          opacity: showSolid ? 0.18 : 0.75,   // subtle in dark mode, vivid in green mode
          zIndex:  0,
        }}
      />

      {/* ── Nav content (sits above canvas) ──────────────────── */}
      <div
        className="relative max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-[72px]"
        style={{ zIndex: 1 }}
      >
        {/* LOGO */}
        <Link ref={logoRef} to="/" className="flex items-center gap-3 group">
          <div className="relative w-[52px] h-[52px] flex-shrink-0">
            <img
              src="/logo.jpeg"
              alt="Phoenix Bird Watching Club logo"
              className="w-full h-full rounded-full object-contain transition-all duration-400 group-hover:scale-105"
              style={{
                background: 'white',
                padding: '2px',
                boxShadow: showSolid
                  ? '0 0 0 2px rgba(251,191,36,0.40), 0 2px 12px rgba(0,0,0,0.35)'
                  : '0 0 0 2px rgba(74,222,128,0.55), 0 2px 12px rgba(34,197,94,0.25)',
              }}
            />
            <span
              className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
              style={{
                boxShadow: showSolid
                  ? '0 0 18px rgba(251,191,36,0.45)'
                  : '0 0 18px rgba(74,222,128,0.55)',
              }}
            />
          </div>

          <div className="flex flex-col leading-none select-none">
            <span
              className="text-[22px] font-black tracking-tight transition-colors duration-300"
              style={{
                fontFamily:     "'Playfair Display', serif",
                letterSpacing:  '-0.02em',
                color: showSolid ? 'white' : '#dcfce7',
              }}
            >
              Phoenix<span style={{ color: showSolid ? '#fbbf24' : '#4ade80' }}>BWC</span>
            </span>
            <span
              className="text-[9px] font-semibold tracking-[0.28em] uppercase mt-0.5 transition-colors duration-300"
              style={{ color: logoSub, fontFamily: "'DM Sans', sans-serif" }}
            >
              Bird Watching Club
            </span>
          </div>
        </Link>

        {/* DESKTOP LINKS */}
        {isSignedIn && (
          <div className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((link, i) => (
              <Link
                key={link}
                ref={el => { linksRef.current[i] = el }}
                to={link === 'Home' ? '/' : `/${link.toLowerCase()}`}
                className="relative text-[12px] font-semibold tracking-[0.1em] uppercase transition-colors duration-300 group pb-1"
                style={{
                  color:      isActive(link)
                    ? (showSolid ? '#fbbf24' : '#4ade80')
                    : linkColor,
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {link}
                {/* active underline */}
                <span
                  className="absolute bottom-0 left-0 h-[2px] rounded-full transition-all duration-300"
                  style={{
                    width:      isActive(link) ? '100%' : '0%',
                    background: showSolid
                      ? 'linear-gradient(90deg,#fbbf24,#f59e0b)'
                      : 'linear-gradient(90deg,#4ade80,#22c55e)',
                  }}
                />
                {/* hover underline */}
                <span
                  className="absolute bottom-0 left-0 h-[2px] rounded-full w-0 group-hover:w-full transition-all duration-300"
                  style={{
                    background: showSolid
                      ? 'rgba(251,191,36,0.35)'
                      : 'rgba(74,222,128,0.40)',
                  }}
                />
              </Link>
            ))}
          </div>
        )}

        {/* AUTH + HAMBURGER */}
        <div className="flex items-center gap-3">
          {isSignedIn ? (
            <>
              <span className="hidden md:block w-px h-5" style={{ background: dividerBg }} />
              <UserButton afterSignOutUrl="/" />
            </>
          ) : (
            <SignInButton mode="modal">
              <button
                className="hidden md:flex items-center gap-2 px-5 py-2 rounded-full font-semibold text-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
                style={{
                  background: showSolid
                    ? 'linear-gradient(135deg,#2e8b2e,#1a5c1a)'
                    : 'linear-gradient(135deg,#16a34a,#15803d)',
                  color:          'white',
                  fontFamily:    "'DM Sans', sans-serif",
                  boxShadow:     showSolid
                    ? '0 4px 18px rgba(46,139,46,0.40)'
                    : '0 4px 18px rgba(74,222,128,0.35)',
                  letterSpacing: '0.03em',
                  border: showSolid ? 'none' : '1px solid rgba(74,222,128,0.35)',
                }}
              >
                <span>🦅</span> Sign In
              </button>
            </SignInButton>
          )}

          {/* Hamburger */}
          <button
            className="md:hidden flex flex-col justify-center items-center gap-[5px] w-9 h-9 rounded-lg transition-all duration-300"
            style={{
              background: mobileOpen
                ? 'rgba(74,222,128,0.15)'
                : (showSolid ? 'rgba(255,255,255,0.06)' : 'rgba(74,222,128,0.10)'),
            }}
            onClick={() => setMobileOpen(v => !v)}
            aria-label="Toggle menu"
          >
            {[0, 1, 2].map((bar) => (
              <span
                key={bar}
                className={`block w-5 h-[2px] rounded-full origin-center transition-all duration-300 ${
                  bar === 0 && mobileOpen ? 'rotate-45 translate-y-[7px]' :
                  bar === 1 && mobileOpen ? 'opacity-0 scale-x-0' :
                  bar === 2 && mobileOpen ? '-rotate-45 -translate-y-[7px]' : ''
                }`}
                style={{ background: showSolid ? 'white' : '#86efac' }}
              />
            ))}
          </button>
        </div>
      </div>

      {/* MOBILE MENU */}
      {mobileOpen && (
        <div
          ref={mobileRef}
          className="md:hidden px-6 py-5"
          style={{
            position:      'relative',
            zIndex:         1,
            background:    'rgba(5,20,6,0.97)',
            backdropFilter: 'blur(28px)',
            borderTop:     '1px solid rgba(74,222,128,0.12)',
          }}
        >
          {isSignedIn
            ? NAV_LINKS.map((link) => (
                <Link
                  key={link}
                  to={link === 'Home' ? '/' : `/${link.toLowerCase()}`}
                  className="flex items-center justify-between py-3.5 border-b text-sm font-medium transition-colors"
                  style={{
                    color:       isActive(link) ? '#4ade80' : 'rgba(255,255,255,0.72)',
                    borderColor: 'rgba(74,222,128,0.08)',
                    fontFamily: "'DM Sans', sans-serif",
                    letterSpacing: '0.05em',
                  }}
                >
                  {link}
                  {isActive(link) && (
                    <span style={{ color: '#4ade80', fontSize: '7px' }}>◆</span>
                  )}
                </Link>
              ))
            : (
                <SignInButton mode="modal">
                  <button
                    className="w-full mt-1 py-3 rounded-full font-semibold text-sm text-white"
                    style={{
                      background:    'linear-gradient(135deg,#16a34a,#15803d)',
                      fontFamily:   "'DM Sans', sans-serif",
                      letterSpacing: '0.04em',
                      border:        '1px solid rgba(74,222,128,0.25)',
                    }}
                  >
                    Sign In to PhoenixBC
                  </button>
                </SignInButton>
              )
          }
        </div>
      )}
    </nav>
  )
}

export default Navbar
